import React, { FC, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CustomMUIButton } from '../../../../buttons/CustomMUIButton';
import { ButtonClassNames } from '../../../../../../models/Common';
import { FormInputOtp } from '../../../../../inputs/FormInputOtp';
import { ApiError } from '../../../../../../models/ApiError';
import { userInfoApi } from '../../../../../../services/apiServices/user/userInfo';
import { useCustomSnackbar } from '../../../../../../hooks/useCustomEnqueueSnackbar';

interface ConfirmNewSmsPhoneProps {
  phoneNumber: string;
  handleClose: () => void;
}

interface CredentialWithCode extends Credential {
  code?: string;
}

export const ConfirmNewSmsPhone: FC<ConfirmNewSmsPhoneProps> = ({ phoneNumber, handleClose }) => {
  const { t } = useTranslation();
  const methods = useForm<{ smsCode: string }>();
  const {
    handleSubmit,
    setValue,
    setError,
    formState: { errors },
  } = methods;
  const { customEnqueueSnackbar } = useCustomSnackbar();
  const [checkConfirmationSmsCodeApi, { isLoading: isCheckCodeLoading }] =
    userInfoApi.useCheckConfirmationSmsCodeMutation();
  const [addNewSmsPhoneApi, { isLoading: isAddNewPhoneLoading }] = userInfoApi.useAddNewSmsPhoneMutation();

  useEffect(() => {
    if ('OTPCredential' in window) {
      const ac = new AbortController();
      const options = { otp: { transport: ['sms'] }, signal: ac.signal };
      navigator.credentials
        .get(options)
        .then((otp: CredentialWithCode | null) => {
          setValue('smsCode', otp?.code ?? '');
          ac.abort();
        })
        .catch(() => {
          ac.abort();
        });
    }
  }, []);

  const handleAddNewPhone = async (phone: string) => {
    try {
      await addNewSmsPhoneApi({ phone })
        .unwrap()
        .then(() => {
          customEnqueueSnackbar(t('snackbar.mainPhoneNumberAddedSuccessfully'), 'success');
          handleClose();
        });
    } catch (err) {
      const error = err as ApiError;
      setError('smsCode', {
        type: 'server',
        message: error?.data?.error.message,
      });
    }
  };

  const handleConfirmSmsCode = async ({ phone = phoneNumber, smsCode }: { phone: string; smsCode: number }) => {
    try {
      await checkConfirmationSmsCodeApi({ phone, code: smsCode })
        .unwrap()
        .then(() => {
          handleAddNewPhone(phone);
        });
    } catch (err) {
      const error = err as ApiError;
      setError('smsCode', {
        type: 'server',
        message: error?.data?.error.message ?? '',
      });
    }
  };

  return (
    <div className="modals">
      <div className="modals__header">
        <article className="modals__header__title">{t('accountSettings.smsCode')}</article>
        <p className="modals__header__description">
          {t('accountSettings.enterVerificationCodeThatWasSentToNumber')} <strong>+38{phoneNumber}</strong>
        </p>
      </div>
      <form className="modals__form" onSubmit={handleSubmit(handleConfirmSmsCode)}>
        <FormProvider {...methods}>
          <FormInputOtp
            name="smsCode"
            inputClassName="primary"
            containerClassName="modals__form__otp"
            isRequired
            minMaxLength={4}
          />
          {errors.smsCode?.message && <p className="modals__form__error">{errors.smsCode?.message}</p>}
          <div className="modals__btn-group">
            <CustomMUIButton
              label={t('buttons.continue')}
              type="submit"
              className={ButtonClassNames.PrimaryFulfilled}
              isBold
              isLoading={isCheckCodeLoading || isAddNewPhoneLoading}
            />
          </div>
        </FormProvider>
      </form>
    </div>
  );
};
